import { EmptyState } from '@bp/ui-components';
import { useAssignments } from 'components/Assignments/hooks/useAssignments';
import { BpCard } from 'components/BpCard/BpCard';
import { FileListDownloader } from 'components/Files/components/FileList/FileListDownloader';
import { TipTapTextExtender } from 'components/TipTap/TipTapTextExtender';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import { getDaysDifference, niceDate } from 'utils/dateCalculations';

type AssignmentMaterialCardProps = {
  assignmentUuid: string;
  showCollapsed?: boolean;
};

export const AssignmentMaterialCard = ({ assignmentUuid, showCollapsed }: AssignmentMaterialCardProps) => {
  const { t } = useTranslation();
  const { courseUuid } = useRequireParams(['courseUuid']);

  const { assignments } = useAssignments({ courseUuid, assignmentUuid });

  const assignment = assignments.find((a) => a.uuid === assignmentUuid);
  if (!assignment) throw new Error('Assignment not found');

  const material = assignment.material;
  const startDate = assignment.visibleFrom;
  const endDate = assignment.dueDate;

  return (
    <BpCard
      noPadding={material?.files && material?.files.length > 0}
      alwaysCollapsible
      isCollapsed={showCollapsed}
      header={{
        headline: t('assignments.task'),
        subHeadline:
          getDaysDifference(startDate, endDate) <= 1
            ? niceDate(startDate, 'short')
            : `${niceDate(startDate, 'short')} - ${niceDate(endDate, 'short')}`,
        showSubHeadlineAsChip: true,
      }}
    >
      {!material ? (
        <EmptyState hideIcon size='small' title={t('assignments.noMaterial')} subtitle='' padding='xl' />
      ) : (
        <>
          {!material.text ? (
            <EmptyState hideIcon title={t('assignments.noTask')} subtitle={t('assignments.noTaskHint')} padding='xl' />
          ) : (
            <TipTapTextExtender
              uuid={material.uuid}
              content={material.text ?? ''}
              padding={material.files.length === 0 ? 'none' : 'default'}
            />
          )}
          {material.files && material.files.length > 0 && (
            <FileListDownloader files={material.files} className='mt-7' />
          )}
        </>
      )}
    </BpCard>
  );
};
